// Path: sec-parser/client/src/components/common/AppLayout.jsx
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Layout, Menu, Typography, Badge, Space, Dropdown, Avatar,
  Button, Drawer,
} from "antd";
import {
  UploadOutlined, FileTextOutlined, ExperimentOutlined,
  DotChartOutlined, SafetyOutlined, TeamOutlined,
  UserOutlined, LogoutOutlined, KeyOutlined, DownOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { logout } from "../../services/api";
import { clearAuth } from "../../store";

const { Sider, Content, Header } = Layout;
const { Text } = Typography;

const NAV_ITEMS = [
  { key: "/upload",       icon: <UploadOutlined />,   label: "Load Filing" },
  { key: "/filings",      icon: <FileTextOutlined />, label: "My Filings" },
  { key: "/review-queue", icon: <SafetyOutlined />,   label: "Review Queue" },
];
const ADMIN_NAV = [{ key: "/users", icon: <TeamOutlined />, label: "User Management" }];

const ALL_MODELS = ["Claude","GPT-4o","Gemini","Groq","Grok","Mistral","DeepSeek","Cohere"];

export default function AppLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();
  const user      = useSelector((s) => s.auth.user);
  const [collapsed, setCollapsed]     = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const selectedKey = "/" + location.pathname.split("/")[1];

  const handleLogout = async () => {
    try { await logout(); } catch {}
    dispatch(clearAuth());
    navigate("/login", { replace: true });
  };

  const userMenuItems = [
    { key: "name",     label: <Text type="secondary" style={{ fontSize: 12 }}>{user?.email}</Text>, disabled: true },
    { type: "divider" },
    { key: "password", label: "Change Password", icon: <KeyOutlined /> },
    { key: "logout",   label: "Sign Out",        icon: <LogoutOutlined />, danger: true },
  ];
  const onUserMenu = ({ key }) => {
    if (key === "logout")   handleLogout();
    if (key === "password") navigate("/change-password");
  };

  const allNav = user?.role === "admin" ? [...NAV_ITEMS, { type: "divider" }, ...ADMIN_NAV] : NAV_ITEMS;

  const SideMenu = ({ inDrawer = false }) => (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "20px 16px 16px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <Space align="center">
          <DotChartOutlined style={{ fontSize: 22, color: "#2E86AB" }} />
          {(!collapsed || inDrawer) && (
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>SEC Parser</div>
              <div style={{ color: "#7aadcc", fontSize: 11 }}>Financial Intelligence</div>
            </div>
          )}
        </Space>
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        onClick={({ key }) => { if (key !== "divider") { navigate(key); setDrawerOpen(false); } }}
        style={{ background: "#0f1f30", marginTop: 8, flex: 1 }}
        items={allNav}
      />

      {(!collapsed || inDrawer) && (
        <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Text style={{ color: "#7aadcc", fontSize: 11 }}>AI Models</Text>
          <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
            {ALL_MODELS.map((m) => (
              <span key={m} style={{ fontSize: 10, color: "#aac", background: "rgba(255,255,255,0.06)", borderRadius: 3, padding: "1px 5px" }}>
                {m}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          collapsible collapsed={collapsed} onCollapse={setCollapsed}
          width={220} style={{ background: "#0f1f30", overflow: "auto", position: "sticky", top: 0, height: "100vh" }}
        >
          <SideMenu />
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
          width={240} bodyStyle={{ background: "#0f1f30", padding: 0 }}
          headerStyle={{ display: "none" }}
        >
          <SideMenu inDrawer />
        </Drawer>
      )}

      <Layout>
        <Header style={{
          background: "#fff", borderBottom: "1px solid #e8ecf0",
          padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <Space>
            {isMobile && (
              <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} />
            )}
            {!isMobile && (
              <Text style={{ fontSize: 14, color: "#1A3C5E", fontWeight: 500 }}>
                <ExperimentOutlined style={{ marginRight: 6, color: "#2E86AB" }} />
                AI-Powered SEC Financial Parser
              </Text>
            )}
            {isMobile && (
              <Text style={{ fontWeight: 700, color: "#1A3C5E" }}>SEC Parser</Text>
            )}
          </Space>

          <Dropdown menu={{ items: userMenuItems, onClick: onUserMenu }} trigger={["click"]}>
            <Button type="text" style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 8px" }}>
              <Avatar size="small" style={{ background: "#1A3C5E", fontSize: 12 }} icon={<UserOutlined />} />
              {!isMobile && <Text style={{ fontSize: 13 }}>{user?.name || "User"}</Text>}
              <Badge
                count={user?.role === "admin" ? "ADMIN" : "ANALYST"}
                style={{ background: user?.role === "admin" ? "#cf1322" : "#1677ff", fontSize: 10, padding: "0 4px" }}
              />
              <DownOutlined style={{ fontSize: 10, color: "#999" }} />
            </Button>
          </Dropdown>
        </Header>

        <Content style={{ padding: isMobile ? 12 : 24, background: "#f0f4f8", minHeight: "calc(100vh - 64px)" }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}